import { useEffect, useState } from "react";
import api, { formatDate, todayISO, apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Calendar as CalIcon, CheckCircle2, Trash2 } from "lucide-react";
import { toast } from "sonner";

const MONTHS_ES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

export default function CalendarioPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", date: todayISO(), event_type: "personal" });

  const load = async () => {
    setLoading(true);
    try {
      const start = new Date(year, month, 1).toISOString().slice(0, 10);
      const end = new Date(year, month + 1, 0).toISOString().slice(0, 10);
      const { data } = await api.get("/calendar", { params: { start, end } });
      setEvents(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [year, month]);

  const submit = async () => {
    try {
      await api.post("/calendar", form);
      toast.success("Evento creado");
      setOpen(false);
      setForm({ title: "", description: "", date: todayISO(), event_type: "personal" });
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const toggle = async (ev) => {
    await api.put(`/calendar/${ev.id}`, { completed: !ev.completed });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este evento?")) return;
    await api.delete(`/calendar/${id}`);
    load();
  };

  const seed = async () => {
    try {
      await api.post(`/calendar/seed/${year}`);
      toast.success("Calendario fiscal actualizado");
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = (new Date(year, month, 1).getDay() + 6) % 7; // Monday-first
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const iso = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    cells.push({ day: d, iso, events: events.filter((e) => e.date === iso) });
  }

  return (
    <div className="space-y-6" data-testid="calendario-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight">
            Calendario fiscal
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Modelos 303, 130, 390, 347, cuota de autónomos y tus eventos personales.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={seed}
            data-testid="seed-fiscal-btn"
          >
            Recargar avisos {year}
          </Button>
          <Button
            className="bg-blue-600 hover:bg-blue-700"
            onClick={() => setOpen(true)}
            data-testid="new-event-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Nuevo evento
          </Button>
        </div>
      </div>

      <Card className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                if (month === 0) {
                  setMonth(11);
                  setYear(year - 1);
                } else setMonth(month - 1);
              }}
              data-testid="prev-month"
            >
              ‹
            </Button>
            <div className="font-display font-bold text-lg min-w-[180px] text-center">
              {MONTHS_ES[month]} {year}
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                if (month === 11) {
                  setMonth(0);
                  setYear(year + 1);
                } else setMonth(month + 1);
              }}
              data-testid="next-month"
            >
              ›
            </Button>
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-500">
            <span className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-amber-500" /> Fiscal
            </span>
            <span className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-blue-500" /> Personal
            </span>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-1 text-xs text-center font-medium text-slate-500 mb-2">
          {["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"].map((d) => (
            <div key={d} className="py-1">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((c, i) => (
            <div
              key={i}
              className={`min-h-[96px] border rounded-md p-1.5 ${
                c ? "bg-white border-slate-200" : "bg-slate-50 border-transparent"
              }`}
            >
              {c && (
                <>
                  <div className="text-xs font-semibold text-slate-500 mb-1">{c.day}</div>
                  <div className="space-y-1">
                    {c.events.slice(0, 3).map((e) => (
                      <div
                        key={e.id}
                        className={`text-[10px] px-1.5 py-0.5 rounded truncate cursor-default ${
                          e.event_type === "fiscal"
                            ? "bg-amber-100 text-amber-800"
                            : "bg-blue-100 text-blue-800"
                        } ${e.completed ? "line-through opacity-60" : ""}`}
                        title={e.title}
                      >
                        {e.title}
                      </div>
                    ))}
                    {c.events.length > 3 && (
                      <div className="text-[10px] text-slate-500">
                        +{c.events.length - 3} más
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-5">
        <div className="mb-3">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">
            Este mes
          </div>
          <div className="font-display text-lg font-bold text-slate-900">
            Eventos y vencimientos
          </div>
        </div>
        {loading ? (
          <div className="text-center py-6 text-slate-500">Cargando...</div>
        ) : events.length === 0 ? (
          <div className="text-center py-8">
            <CalIcon className="w-8 h-8 text-slate-300 mx-auto" />
            <p className="text-sm text-slate-500 mt-2">Sin eventos este mes.</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {events.map((e) => (
              <li
                key={e.id}
                className="py-3 flex items-center gap-3"
                data-testid={`event-${e.id}`}
              >
                <button
                  onClick={() => toggle(e)}
                  className={`w-6 h-6 rounded-full border grid place-items-center transition ${
                    e.completed
                      ? "bg-emerald-600 border-emerald-600 text-white"
                      : "border-slate-300 text-transparent hover:border-slate-500"
                  }`}
                  data-testid={`event-check-${e.id}`}
                >
                  <CheckCircle2 className="w-4 h-4" />
                </button>
                <div className="flex-1 min-w-0">
                  <div
                    className={`font-medium text-sm ${
                      e.completed ? "line-through text-slate-500" : "text-slate-900"
                    }`}
                  >
                    {e.title}
                  </div>
                  {e.description && (
                    <div className="text-xs text-slate-500 truncate">{e.description}</div>
                  )}
                </div>
                <div className="text-xs text-slate-500 whitespace-nowrap">
                  {formatDate(e.date)}
                </div>
                <span
                  className={`text-[10px] px-1.5 py-0.5 rounded ${
                    e.event_type === "fiscal"
                      ? "bg-amber-100 text-amber-800"
                      : "bg-blue-100 text-blue-800"
                  }`}
                >
                  {e.event_type === "fiscal" ? e.tax_model || "Fiscal" : "Personal"}
                </span>
                {e.event_type === "personal" && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => remove(e.id)}
                    className="text-rose-500 hover:text-rose-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuevo evento</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Título</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                data-testid="event-title-input"
              />
            </div>
            <div>
              <Label>Fecha</Label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
              />
            </div>
            <div>
              <Label>Tipo</Label>
              <Select
                value={form.event_type}
                onValueChange={(v) => setForm({ ...form, event_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="personal">Personal</SelectItem>
                  <SelectItem value="fiscal">Fiscal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Descripción</Label>
              <Textarea
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={submit}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="event-submit-btn"
            >
              Crear
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
